import React, { useState } from 'react';
import { Search, RefreshCw, Calendar } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Alert from '../ui/Alert';
import adminService from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';

const ExtensionManager = () => {
  const { user } = useAuth();
  const [searchEmail, setSearchEmail] = useState('');
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSearchUser = async () => {
    if (!searchEmail.trim()) {
      setMessage({ type: 'error', text: 'Please enter an email address' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const allUsersResult = await adminService.getAllUsers();
      
      if (!allUsersResult.success) {
        setMessage({ type: 'error', text: 'Failed to load users: ' + allUsersResult.error });
        setLoading(false);
        return;
      }

      const foundUser = allUsersResult.users.find(u => 
        u.email && u.email.toLowerCase() === searchEmail.trim().toLowerCase()
      );

      if (!foundUser) {
        setUserStatus(null);
        setMessage({ type: 'error', text: `No user found with email: ${searchEmail.trim()}` });
        setLoading(false);
        return;
      }

      const result = await adminService.getUserExtensionStatus(foundUser.userId);
      
      if (result.success) {
        setUserStatus(result.data);
        setMessage({ type: 'success', text: 'User found successfully' });
      } else {
        setUserStatus(null);
        setMessage({ type: 'error', text: result.error || 'Failed to get extension status' });
      }
    } catch (error) {
      setUserStatus(null);
      setMessage({ type: 'error', text: 'Error searching for user: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleResetExtension = async () => {
    if (!userStatus) return;

    setLoading(true);
    setMessage(null);

    try {
      const result = await adminService.resetUserExtension(userStatus.userId, user.sub);
      
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        await handleSearchUser();
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to reset extension' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error resetting extension: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGrantExtension = async () => {
    if (!userStatus) return;

    setLoading(true);
    setMessage(null);

    try {
      const extensionData = {
        reason: 'Admin granted extension',
        extensionMonths: 12
      };

      const result = await adminService.grantExtension(userStatus.userId, extensionData, user.sub);
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Extension granted successfully' });
        await handleSearchUser();
      } else {
        setMessage({ type: 'error', text: result.error || 'Failed to grant extension' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error granting extension: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Calendar className="w-5 h-5 text-blue-600" />
        <h2 className="text-xl font-semibold text-gray-900">Extension Manager</h2>
      </div>

      {/* Search User */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Search User by Email Address
        </label>
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="Enter email address"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            disabled={loading}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearchUser();
              }
            }}
          />
          <Button
            onClick={handleSearchUser}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            Search
          </Button>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <Alert variant={message.type}>
          {message.text}
        </Alert>
      )}

      {/* User Status */}
      {userStatus && (
        <div className="border border-gray-200 rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">User Extension Status</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="text-base font-medium text-gray-900">{userStatus.email}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500">Name</p>
              <p className="text-base font-medium text-gray-900">{userStatus.name}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500">Extension Used</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                userStatus.extensionUsed ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
              }`}>
                {userStatus.extensionUsed ? 'Yes' : 'No'}
              </span>
            </div>
            
            <div>
              <p className="text-sm text-gray-500">Cycle Expiration</p>
              <p className="text-base font-medium text-gray-900">
                {userStatus.currentCycleEnd || 'Not set'}
              </p>
            </div>
            
            {userStatus.extensionGrantedDate && (
              <>
                <div>
                  <p className="text-sm text-gray-500">Extension Granted</p>
                  <p className="text-base font-medium text-gray-900">
                    {new Date(userStatus.extensionGrantedDate).toLocaleDateString()}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Granted By</p>
                  <p className="text-base font-medium text-gray-900">
                    {userStatus.extensionGrantedBy || 'Unknown'}
                  </p>
                </div>
              </>
            )}
            
            {userStatus.extensionResetAt && (
              <>
                <div>
                  <p className="text-sm text-gray-500">Last Reset</p>
                  <p className="text-base font-medium text-gray-900">
                    {new Date(userStatus.extensionResetAt).toLocaleDateString()}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Reset By</p>
                  <p className="text-base font-medium text-gray-900">
                    {userStatus.extensionResetBy || 'Unknown'}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={handleResetExtension}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reset Extension Status
            </Button>
            
            {!userStatus.extensionUsed && (
              <Button
                onClick={handleGrantExtension}
                disabled={loading}
                className="flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                Grant Extension
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtensionManager;
